import React from "react";
import { Page, Text, StyleSheet, View, Image } from "@react-pdf/renderer";
import { ImageProps, PullingDesignProps } from "../../../../interfaces/interfaces";
import { Header } from "../../components/Header";
import Footer from "../../components/Footer";

// ── Column widths ─────────────────────────────────────────────────────────────
const COL_DESCRIPTION = "30%";
const COL_QTY = "5%";
const COL_PHOTOS = "50%";
const COL_COMMENTS = "15%";

const BORDER = "0.5px solid black";
const BORDER_THIN = "0.25px solid black";

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  page: { paddingTop: 80, paddingLeft: 20, paddingRight: 20, paddingBottom: 80 },

  // Info block (title + well details)
  infoSection: { marginTop: 20, marginLeft: 30, marginRight: 30 },
  reportTitle:  { fontSize: 13, fontFamily: "Helvetica-Bold", marginBottom: 3 },
  infoLine:     { fontSize: 11, fontFamily: "Helvetica", marginBottom: 2 },
  toolsHeader:  { fontSize: 12, fontFamily: "Helvetica-Bold", marginTop: 10 },

  // Table structure
  table:          { marginTop: 8, width: "100%" },
  tableHeaderRow: { flexDirection: "row", borderTop: BORDER, borderBottom: BORDER, borderLeft: BORDER, borderRight: BORDER },
  tableRow:       { flexDirection: "row", borderBottom: BORDER_THIN, borderLeft: BORDER, borderRight: BORDER, minHeight: 40 },

  // Cells
  cellDescription: { width: COL_DESCRIPTION, borderRight: BORDER, padding: 4, fontSize: 9, fontFamily: "Helvetica", justifyContent: "center" },
  cellQty:         { width: COL_QTY,         borderRight: BORDER, padding: 4, fontSize: 9, fontFamily: "Helvetica", alignItems: "center", justifyContent: "center" },
  cellPhotos:      { width: COL_PHOTOS,      borderRight: BORDER, padding: 4 },
  cellComments:    { width: COL_COMMENTS,    padding: 6, fontSize: 9, fontFamily: "Helvetica" },

  // Header cell text
  headerCellText: { fontSize: 9, fontFamily: "Helvetica-Bold", textAlign: "center" },

  // Photo grid (2 per row)
  photoRow:  { flexDirection: "row", justifyContent: "center", marginBottom: 4 },
  photoCell: { alignItems: "center", justifyContent: "center", padding: 2 },

  // Comment bullet
  commentItem: { fontSize: 9, marginBottom: 4, lineHeight: 1.4, fontFamily: "Helvetica" },

  // Recommendations section
  recSection: { marginTop: 16, marginLeft: 30, marginRight: 30 },
  recTitle:   { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 8 },
  recItem:    { flexDirection: "row", marginBottom: 6 },
  recBullet:  { fontSize: 10, width: 14, fontFamily: "Helvetica" },
  recText:    { flex: 1, fontSize: 10, fontFamily: "Helvetica", lineHeight: 1.4 },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function chunk<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
}

// Maximum dimensions per photo slot:
// Photos cell = 50% of ~555pt available width = ~270pt inner.
// For 2 images side by side: each slot = ~128pt wide.
// Height capped so tall images don't create page-breaking rows.
const PHOTO_SLOT_W = 128;
const PHOTO_SLOT_H = 160;

function getImageDimensions(image: ImageProps) {
  const scale = image.scale || 1;

  // Intended natural dimensions per size category (determines aspect ratio).
  // These are "natural" target sizes — the fit-ratio below scales them down to
  // stay within PHOTO_SLOT_W × PHOTO_SLOT_H without distorting the image.
  //   1 / default → Landscape (3:2)    fills slot width
  //   2           → Wide landscape (2:1) fills slot width, shorter
  //   3           → Square (1:1)
  //   4 / 5       → Portrait (2:3)     fills slot height
  let nw: number, nh: number;
  switch (image.size) {
    case 2:  [nw, nh] = [180 * scale, 90  * scale]; break; // wide landscape
    case 3:  [nw, nh] = [128 * scale, 128 * scale]; break; // square
    case 4:
    case 5:  [nw, nh] = [107 * scale, 160 * scale]; break; // portrait fills height
    default: [nw, nh] = [200 * scale, 130 * scale]; break; // standard landscape
  }

  // Scale down uniformly to fit inside the slot; never scale up
  const ratio = Math.min(PHOTO_SLOT_W / nw, PHOTO_SLOT_H / nh, 1);
  return { width: Math.floor(nw * ratio), height: Math.floor(nh * ratio) };
}

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
}

function formatDate(d: string): string {
  return d ? new Date(d).toLocaleDateString("en-US") : "";
}

// ── Component ─────────────────────────────────────────────────────────────────

export const PullingPage = ({ pullingInfo }: { pullingInfo: PullingDesignProps }) => {
  const clientName      = pullingInfo.well.client?.name ?? "";
  const activeDetails   = pullingInfo.details.filter(d => d.description.id !== 0);
  const recommendations = pullingInfo.recomendations.filter(Boolean);

  return (
    <Page style={styles.page}>
      <Header showDate={false} clientName={clientName} />

      {/* ── Report header block ── */}
      <View style={styles.infoSection}>
        <Text style={styles.reportTitle}>Pulling Integrity Tool Report</Text>
        <Text style={styles.infoLine}>{clientName}</Text>
        <Text style={styles.infoLine}>Well: {pullingInfo.well.name}</Text>
        <Text style={styles.infoLine}>Installation Date: {formatDate(pullingInfo.installationDate)}</Text>
        <Text style={styles.infoLine}>Pulling Date: {formatDate(pullingInfo.pullingDate)}</Text>
        <Text style={styles.infoLine}>
          Runtime: {daysBetween(pullingInfo.installationDate, pullingInfo.pullingDate)} days
        </Text>
        <Text style={styles.toolsHeader}>TOOLS RECOVERED</Text>
      </View>

      {/* ── Tools table ── */}
      <View style={styles.table}>

        {/* Header row */}
        <View style={styles.tableHeaderRow}>
          <View style={[styles.cellDescription, { alignItems: "center" }]}>
            <Text style={styles.headerCellText}>DESCRIPTION</Text>
          </View>
          <View style={styles.cellQty}>
            <Text style={styles.headerCellText}>QTY</Text>
          </View>
          <View style={[styles.cellPhotos, { alignItems: "center" }]}>
            <Text style={styles.headerCellText}>PHOTOGRAPHIC RECORD</Text>
          </View>
          <View style={[styles.cellComments, { alignItems: "center" }]}>
            <Text style={styles.headerCellText}>COMMENTS</Text>
          </View>
        </View>

        {/* Data rows — wrap={false} keeps each row on one page */}
        {activeDetails.map((detail, i) => {
          const photos = detail.pictures.filter(p => p.image);
          return (
            <View key={i} style={styles.tableRow} wrap={false}>

              <View style={styles.cellDescription}>
                <Text>{detail.description.name || detail.description.description}</Text>
                {detail.toolName ? <Text>{detail.toolName}</Text> : null}
              </View>

              <View style={styles.cellQty}>
                <Text>{detail.quantity}</Text>
              </View>

              {/* Photos rendered 2-per-row via chunk() */}
              <View style={styles.cellPhotos}>
                {chunk(photos, 2).map((pair, ri) => (
                  <View key={ri} style={styles.photoRow}>
                    {pair.map((photo, ci) => (
                      <View key={ci} style={styles.photoCell}>
                        <Image src={photo.image} style={getImageDimensions(photo)} />
                      </View>
                    ))}
                  </View>
                ))}
              </View>

              <View style={styles.cellComments}>
                {detail.comment.filter(Boolean).map((c, ci) => (
                  <Text key={ci} style={styles.commentItem}>• {c}</Text>
                ))}
              </View>

            </View>
          );
        })}
      </View>

      {/* ── Recommendations (flows after table, same page or next if overflow) ── */}
      {recommendations.length > 0 && (
        <View style={styles.recSection}>
          <Text style={styles.recTitle}>RECOMMENDATIONS AND COMMENTS</Text>
          {recommendations.map((rec, i) => (
            <View key={i} style={styles.recItem}>
              <Text style={styles.recBullet}>•</Text>
              <Text style={styles.recText}>{rec}</Text>
            </View>
          ))}
        </View>
      )}

      <Footer />
    </Page>
  );
};
